import * as fs from 'fs-extra';
import * as path from 'path';
import { logger } from '../utils/logger';
import { 
  getFullWorkflowPermissions, 
  addPermissions 
} from '../utils/claude-permissions';

export interface ClaudeIntegrationOptions {
  projectRoot: string;
  force?: boolean;
  enableHooks?: boolean;
  safeMode?: boolean;
}

export interface ClaudeSettings {
  permissions?: {
    allow: string[];
    deny: string[];
  };
  enableAllProjectMcpServers?: boolean;
  enabledMcpjsonServers?: string[];
  hooks?: Record<string, any>;
}

export class ClaudeIntegration {
  private claudeDir: string;
  private settingsPath: string;
  private localSettingsPath: string;

  constructor(private options: ClaudeIntegrationOptions) {
    this.claudeDir = path.join(options.projectRoot, '.claude');
    this.settingsPath = path.join(this.claudeDir, 'settings.json');
    this.localSettingsPath = path.join(this.claudeDir, 'settings.local.json');
  }

  /**
   * Check if Claude Code is configured in the project
   */
  async isClaudeProject(): Promise<boolean> {
    return await fs.pathExists(this.claudeDir);
  }

  /**
   * Initialize Claude Code integration
   */
  async initialize(): Promise<void> {
    // Ensure .claude directory exists
    await fs.ensureDir(this.claudeDir);
    await fs.ensureDir(path.join(this.claudeDir, 'commands'));
    await fs.ensureDir(path.join(this.claudeDir, 'hooks'));
    await fs.ensureDir(path.join(this.claudeDir, 'logs'));

    // Create or update settings
    await this.updateSettings();

    // Create workflow command shortcuts
    await this.createCommandShortcuts();

    // Configure safe hooks if enabled and not explicitly disabled
    if (this.options.enableHooks && process.env.WORKFLOW_NO_HOOKS !== 'true') {
      await this.configureSafeHooks();
    }
  }

  /**
   * Update Claude Code settings to include workflow permissions
   */
  private async updateSettings(): Promise<void> {
    let settings: ClaudeSettings = {};

    // Load existing settings if present
    if (await fs.pathExists(this.localSettingsPath)) {
      try {
        settings = await fs.readJson(this.localSettingsPath);
      } catch (error) {
        logger.warn('Failed to read existing settings, creating new ones');
      }
    }

    // Ensure permissions structure exists
    if (!settings.permissions) {
      settings.permissions = { allow: [], deny: [] };
    }

    // 获取完整的 workflow-cli 权限集
    const requiredPermissions = getFullWorkflowPermissions();
    
    // 添加项目特定的权限
    const projectSpecificPermissions = [
      `Bash(${path.join(this.options.projectRoot, 'packages/workflow-cli/dist/cli.js')}:*)`,
      `Bash(${path.join(this.options.projectRoot, '.workflow')}:*)`,
      'Read(.workflow/**)',
      'Write(.workflow/**)',
      'Edit(.workflow/**)',
      'Read(.claude/**)',
      'Write(.claude/**)',
      'Edit(.claude/**)'
    ];
    
    // 合并所有权限
    const allPermissions = [...requiredPermissions, ...projectSpecificPermissions];
    
    // 使用权限管理工具添加权限
    settings.permissions = addPermissions(
      settings.permissions || { allow: [], deny: [] },
      allPermissions
    );

    // Enable MCP servers if not configured
    if (settings.enableAllProjectMcpServers === undefined) {
      settings.enableAllProjectMcpServers = true;
    }

    // Save updated settings
    await fs.writeJson(this.localSettingsPath, settings, { spaces: 2 });
    logger.info('Updated Claude Code settings with workflow permissions');
  }

  /**
   * Create command shortcuts in .claude/commands
   */
  private async createCommandShortcuts(): Promise<void> {
    const commandsDir = path.join(this.claudeDir, 'commands');

    // Workflow initialization command
    const workflowInitCommand = `# /workflow-init

Initialize and configure the workflow system for this project.

## Usage
Run \`/workflow-init\` to set up AI-powered workflow management.

## Implementation
\`\`\`bash
cd "${this.options.projectRoot}"
npx @codepack/workflow-cli init
\`\`\`
`;

    // Task management command
    const taskCommand = `# /task

Quick task management using workflow-cli.

## Usage
- \`/task start "Task description"\` - Start a new task
- \`/task status\` - Check current task status
- \`/task complete\` - Complete the current task
- \`/task list\` - List recent tasks

## Implementation
\`\`\`bash
cd "${this.options.projectRoot}"
npx @codepack/workflow-cli task $1
\`\`\`
`;

    // Knowledge base command
    const kbCommand = `# /kb

Search and manage the project knowledge base.

## Usage
- \`/kb search "term"\` - Search knowledge base
- \`/kb add pattern "Title" "Content"\` - Add new pattern

## Implementation
\`\`\`bash
cd "${this.options.projectRoot}"
npx @codepack/workflow-cli kb $1
\`\`\`
`;

    // Write command files
    await fs.writeFile(path.join(commandsDir, 'workflow-init.md'), workflowInitCommand);
    await fs.writeFile(path.join(commandsDir, 'task.md'), taskCommand);
    await fs.writeFile(path.join(commandsDir, 'kb.md'), kbCommand);

    logger.info('Created workflow command shortcuts in .claude/commands');
  }

  /**
   * Configure safe hooks for workflow integration
   */
  private async configureSafeHooks(): Promise<void> {
    const settings = await fs.readJson(this.localSettingsPath);

    // Define safe hooks configuration
    const safeHooks = {
      PostToolUse: [
        {
          matcher: '^TodoWrite',
          hooks: [
            {
              type: 'command',
              command: `cd "${this.options.projectRoot}" && npx @codepack/workflow-cli task list --quiet`,
              timeout: 3
            }
          ]
        }
      ],
      Stop: [
        {
          hooks: [
            {
              type: 'command',
              command: `echo "[$(date)] Session ended" >> "${path.join(this.claudeDir, 'logs', 'sessions.log')}"`,
              timeout: 1
            }
          ]
        }
      ]
    };

    // Only add hooks in safe mode or if explicitly requested
    if (this.options.safeMode) {
      settings.hooks = safeHooks;
    } else {
      // Merge with existing hooks
      settings.hooks = { ...settings.hooks, ...safeHooks };
    }

    await fs.writeJson(this.localSettingsPath, settings, { spaces: 2 });
    logger.info('Configured safe workflow hooks');
  }

  /**
   * Create a symlink from .workflow to .claude/workflow for better integration
   */
  async createWorkflowSymlink(): Promise<void> {
    const workflowDir = path.join(this.options.projectRoot, '.workflow');
    const claudeWorkflowLink = path.join(this.claudeDir, 'workflow');

    // Remove existing workflow symlink if it exists
    if (await fs.pathExists(claudeWorkflowLink)) {
      const stats = await fs.lstat(claudeWorkflowLink);
      if (stats.isSymbolicLink()) {
        await fs.unlink(claudeWorkflowLink);
      }
    }

    // Create new symlink
    if (await fs.pathExists(workflowDir)) {
      try {
        await fs.symlink(workflowDir, claudeWorkflowLink, 'dir');
        logger.info('Created symlink from .workflow to .claude/workflow');
      } catch (error) {
        logger.warn('Failed to create symlink, skipping');
      }
    }
  }
}